import { __, REFERRAL_CODE_MAX_LENGTH } from '../functions/commom';
import DateSelector from './base/DateSelector';
import { useInsuranceState } from '../state/state';
import {
  DATE_FORMAT,
  IDENTITY_TYPE,
  MACAU_GEOS,
  AGE_REQUIREMENTS,
  SOURCE_OF_BUSINESS,
  SOURCE_OF_BUSINESS_OTHER,
} from '../config';
import InputBox from './base/InputBox';
import SelectorBox from './base/SelectorBox';
import moment from 'moment-timezone';

interface ComponentsMakerProps {
  component: string;
  errorMessage?: string;
}

const ComponentsMaker: React.FC<ComponentsMakerProps> = ({
  component,
  errorMessage,
}) => {
  const state = useInsuranceState();

  const handleFormDataChange = (
    name: string,
    value: string | Date | null | undefined
  ) => {
    switch (name) {
      case 'name':
        state.form.name.set(value as string);
        break;
      case 'gender':
        state.form.gender.set(value as string);
        break;
      case 'dateOfBirth':
        state.form.dateOfBirth.set(value as string);
        break;
      case 'idType':
        state.form.idType.set(value as string);
        break;
      case 'id':
        state.form.id.set(value as string);
        break;
      case 'sourceOfBusiness':
        state.form.sourceOfBusiness.set(value as string);
        break;
      case 'sourceOfBusinessOther':
        state.form.sourceOfBusinessOther.set(value as string);
        break;
      case 'address':
        state.form.address.set(value as string);
        break;
      case 'phone':
        state.form.phone.set(value as string);
        break;
      case 'email':
        state.form.email.set(value as string);
        break;
      case 'referralCode':
        state.form.referralCode.set(
          ((value as string) || '').slice(0, REFERRAL_CODE_MAX_LENGTH)
        );
        break;
      case 'insuredPropertyRegion':
        state.form.insuredPropertyRegion.set(value as string);
        break;
      case 'insuredPropertyAddress':
        state.form.insuredPropertyAddress.set(value as string);
        break;
      default:
        console.warn(`Field ${name} not found in form state`);
    }
  };

  switch (component) {
    case 'name': {
      return (
        <InputBox
          label={__('Name (English)')}
          value={state.form.name.get() || ''}
          onChange={(value) => handleFormDataChange('name', value)}
          placeholder="e.g. CHAN Wai Lok"
          errorMessage={errorMessage}
          required
        />
      );
    }

    case 'gender': {
      return (
        <SelectorBox
          label={__('Gender')}
          options={[
            { label: __('Male'), value: 'm' },
            { label: __('Female'), value: 'f' },
          ]}
          onChange={(value) => handleFormDataChange('gender', value)}
          value={state.form.gender.get() || undefined}
          errorMessage={errorMessage}
          required
        />
      );
    }

    case 'idType': {
      return (
        <SelectorBox
          label={__('Identity Type')}
          value={state.form.idType.get() || undefined}
          onChange={(value) => handleFormDataChange('idType', value)}
          options={IDENTITY_TYPE}
          errorMessage={errorMessage}
          required
        />
      );
    }

    case 'id': {
      return (
        <InputBox
          label={__('ID / Passport No.')}
          value={state.form.id.get() || ''}
          onChange={(value) => handleFormDataChange('id', value)}
          placeholder={__('ID / Passport No.')}
          errorMessage={errorMessage}
          required
        />
      );
    }

    case 'sourceOfBusiness': {
      const sourceOfBusiness = state.form.sourceOfBusiness.get();
      const isOther = sourceOfBusiness === SOURCE_OF_BUSINESS_OTHER;

      return (
        <>
          <SelectorBox
            label={__('How did you hear about our travel insurance product?')}
            onChange={(value) => {
              handleFormDataChange('sourceOfBusiness', value);
              if (value !== SOURCE_OF_BUSINESS_OTHER) {
                handleFormDataChange('sourceOfBusinessOther', '');
              }
            }}
            value={sourceOfBusiness || undefined}
            errorMessage={isOther ? undefined : errorMessage}
            options={SOURCE_OF_BUSINESS.map((option) => ({
              label: __(option.label),
              value: option.value,
            }))}
            // Not required
          />
          {isOther && (
            <InputBox
              label={__('Please specify')}
              value={state.form.sourceOfBusinessOther.get() || ''}
              placeholder={__('Please specify')}
              errorMessage={errorMessage}
              onChange={(value) =>
                handleFormDataChange('sourceOfBusinessOther', value)
              }
              required
            />
          )}
        </>
      );
    }

    case 'address': {
      return (
        <InputBox
          label={__('Corresponding Address')}
          value={state.form.address.get() || ''}
          placeholder={__('Complete address')}
          errorMessage={errorMessage}
          onChange={(value) => handleFormDataChange('address', value)}
          required
        />
      );
    }

    case 'insuredPropertyRegion': {
      return (
        <SelectorBox
          label={__('Insured Property Region')}
          onChange={(value) =>
            handleFormDataChange('insuredPropertyRegion', value)
          }
          value={state.form.insuredPropertyRegion.get() || undefined}
          errorMessage={errorMessage}
          options={MACAU_GEOS.map((geo) => ({
            label: __(geo),
            value: geo,
          }))}
          required
        />
      );
    }

    case 'insuredPropertyAddress': {
      return (
        <InputBox
          label={__('Insured Property Address')}
          value={state.form.insuredPropertyAddress.get() || ''}
          placeholder={__('Complete address')}
          errorMessage={errorMessage}
          onChange={(value) =>
            handleFormDataChange('insuredPropertyAddress', value)
          }
          required
        />
      );
    }

    case 'phone': {
      return (
        <InputBox
          label={__('Phone')}
          value={state.form.phone.get() || ''}
          onChange={(value) => handleFormDataChange('phone', value)}
          prefix="+853"
          placeholder={__('Macau phone number e.g. 61234567')}
          errorMessage={errorMessage}
          required
        />
      );
    }

    case 'email': {
      return (
        <InputBox
          label={__('Email')}
          value={state.form.email.get() || ''}
          onChange={(value) => handleFormDataChange('email', value)}
          placeholder={__('e.g. 12345678@123.com')}
          errorMessage={errorMessage}
          required
        />
      );
    }

    case 'referralCode': {
      return (
        <InputBox
          label={__('Referral Code')}
          value={state.form.referralCode.get() || ''}
          onChange={(value) => handleFormDataChange('referralCode', value)}
          maxLength={REFERRAL_CODE_MAX_LENGTH}
          errorMessage={errorMessage}
        />
      );
    }

    case 'dateOfBirth': {
      // Policy holder must be at least 18 years old
      const maxDateForAdult = moment()
        .subtract(AGE_REQUIREMENTS.adult.minAgeYears, 'years')
        .format(DATE_FORMAT);
      
      return (
        <DateSelector
          label={__('Date of Birth')}
          onChange={(value) => handleFormDataChange('dateOfBirth', value)}
          maxDate={maxDateForAdult}
          minDate={moment().subtract(120, 'years').format(DATE_FORMAT)}
          value={state.form.dateOfBirth.get() || null}
          errorMessage={errorMessage}
          required
        />
      );
    }

    default:
      return null;
  }
};

export { ComponentsMaker };
